import { useEffect, useRef, useState, type ReactNode } from "react";
import { t, useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
import { Heart, Play, SlidersHorizontal, Star } from "lucide-react";
import type { DiscoverySort } from "./types";

export const SORT_LABELS: Record<DiscoverySort, string> = {
  recommended: "Recommended",
  for_you: "For You",
  new: "New",
  rating: "Top rated",
  popular: "Popular",
};

export default function DiscoveryFilters({ categories, category, onCategory, sort, onSort, allowRatings, view, search = null }: {
  categories: readonly { id: string; label: string }[];
  category: string | null;
  onCategory: (id: string | null) => void;
  sort: DiscoverySort;
  onSort: (sort: DiscoverySort) => void;
  allowRatings: boolean;
  /** Layout marker: the Grid page has its own browse bar; Preview keeps the
   *  chrome inside the panel. There is no manual view switch anymore. */
  view: "play" | "list";
  /** The search field; shares a row with the categories on wide screens. */
  search?: ReactNode;
  /** Lead section rendered between the categories and the sorted grid. */
}) {
  useLocale();
  const chips = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  // Fade the row's end while chips remain past it, so a phone shows the row scrolls.
  useEffect(() => {
    const row = chips.current;
    if (!row) return;
    const measure = () => setMore(row.scrollLeft + row.clientWidth < row.scrollWidth - 1);
    measure();
    row.addEventListener("scroll", measure, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(row);
    return () => {
      row.removeEventListener("scroll", measure);
      observer?.disconnect();
    };
  }, [categories.length]);
  // Search (narrow screens only; wide screens search from the header), then
  // categories and the sort menu. The sort stays a native select inside the
  // menu; a dot marks a non-default order.
  const searchRow = (
      <div className="discovery-search-row">
        {search}
      </div>
  );
  const chipRow = (
      <div className="discovery-chip-row">
        <div ref={chips} className={`discovery-categories${more ? " discovery-categories--more" : ""}`} role="group" aria-label={t("Category")}>
          <button type="button" className="ato-chip" aria-pressed={!category} onClick={() => onCategory(null)}><Message message="All" /></button>
          {categories.map(item => <button key={item.id} type="button" className="ato-chip" aria-pressed={category === item.id}
            onClick={() => onCategory(item.id)}>{t(item.label)}</button>)}
        </div>
        <details className="discovery-sort-menu">
          <summary className={`discovery-sort-button${sort === "recommended" ? "" : " discovery-sort-button--active"}`}
            aria-label={`${t("Sort apps")}: ${t(SORT_LABELS[sort])}`}
            title={`${t("Sort apps")}: ${t(SORT_LABELS[sort])}`}>
            <SlidersHorizontal size={19} aria-hidden="true" />
            <span className="discovery-sort-label">{t(SORT_LABELS[sort])}</span>
          </summary>
          <div className="discovery-sort-popover">
            <label>
              <span><Message message="Sort apps" /></span>
              <select className="ato-select discovery-feed-sort" aria-label={t("Sort apps")} value={sort}
                onChange={event => {
                  onSort(event.target.value as DiscoverySort);
                  event.currentTarget.closest("details")?.removeAttribute("open");
                }}>
                <option value="recommended"><Message message="Recommended" /></option>
                <option value="for_you"><Message message="For You" /></option>
                <option value="new"><Message message="New" /></option>
                {allowRatings ? <option value="rating"><Message message="Top rated" /></option> : null}
              </select>
            </label>
          </div>
        </details>
      </div>
  );
  // The cards are deliberately compact (AI / heart / play / author), so the
  // feed names what those figures are once instead of every card repeating
  // labels. The order line stays next to the sort that caused the change.
  const figureLegend = (
    <p className="discovery-figure-legend">
      {allowRatings ? <span><Star size={12} aria-hidden="true" /><Message message="AI rating" /></span> : null}
      <span><Heart size={12} aria-hidden="true" /><Message message="Likes" /></span>
      <span><Play size={12} aria-hidden="true" /><Message message="Runs" /></span>
    </p>
  );
  const sortNote = sort === "rating" ? (
    <p className="discovery-sort-note" role="status">
      {t("Sorted by {{value0}} — only apps with an AI rating are included.", {
        value0: t(SORT_LABELS[sort]),
      })}
    </p>
  ) : null;
  // Grid reads top-down: the categories that filter the list below it, then
  // the list (Featured is a row inside it). Preview's panel keeps them on top.
  if (view === "list") return <div className="discovery-filters">
    <div className="discovery-browse-bar discovery-browse-bar--search">{searchRow}</div>
    <div className="discovery-browse-bar">{chipRow}</div>
    {sortNote}
    {figureLegend}
    <div className="discovery-feed-heading">
      <h2 className="ato-section-title discovery-explore-title" tabIndex={-1}><Message message="Explore" /></h2>
    </div>
  </div>;
  return <div className="discovery-filters">
    <div className="discovery-browse-bar">{searchRow}{chipRow}{sortNote}</div>
    {figureLegend}
  </div>;
}
